import { ObjectOptions } from 'api/requestUtils'

export const getOptions = (accessToken?: string): ObjectOptions =>
  accessToken ?
      { headers: { Authorization: `Bearer ${accessToken}` } } :
      { credentials: 'include' }

export const baseUri = '/api/trips'
export const baseTemplatesUri = `${baseUri}/templates`

// Max length for user-friendly error messages (longer messages are likely technical)
const MAX_USER_FRIENDLY_MESSAGE_LENGTH = 200

/**
 * Extract a user-friendly error message from an API error response.
 * Follows the codebase's extractError pattern from reducers.
 *
 * API error structure: { status, message, errors: [{ message, path, value }] }
 */
export function extractApiErrorMessage(errorText: string, statusCode: number): string {
  // Default messages by status code
  const defaultMessages: Record<number, string> = {
    400: 'Invalid request. Please check your input and try again.',
    401: 'Please sign in to continue.',
    403: 'You don\'t have permission to perform this action.',
    404: 'The requested resource was not found.',
    422: 'Unable to process your request. Please try again.',
    500: 'Something went wrong on our end. Please try again later.',
    502: 'Service temporarily unavailable. Please try again later.',
    503: 'Service temporarily unavailable. Please try again later.',
  }

  const fallbackMessage = defaultMessages[statusCode] ?? 'Something went wrong. Please try again.'

  if (!errorText) {
    return fallbackMessage
  }

  try {
    const parsed = JSON.parse(errorText)

    // Handle errors array (Strummer validation errors)
    if (Array.isArray(parsed.errors) && parsed.errors.length > 0) {
      const firstError = parsed.errors[0]
      if (firstError.message) {
        // For validation errors, show the specific field error
        // e.g., "userMessageId should be a UUID" -> just show generic message
        // as field names are technical and not user-friendly
        return fallbackMessage
      }
    }

    // Use the top-level message if available and user-friendly
    if (parsed.message && typeof parsed.message === 'string') {
      // Check if message looks technical (contains JSON, code terms, etc.)
      const technicalPatterns = ['{', '[', 'undefined', 'null', 'NaN', 'Error:']
      const isTechnical = technicalPatterns.some(p => parsed.message.includes(p))
      if (!isTechnical && parsed.message.length < MAX_USER_FRIENDLY_MESSAGE_LENGTH) {
        return parsed.message
      }
    }

    return fallbackMessage
  } catch {
    // Not valid JSON - check if it's a clean text message
    if (errorText.length < MAX_USER_FRIENDLY_MESSAGE_LENGTH && !errorText.includes('{') && !errorText.includes('<')) {
      return errorText
    }
    return fallbackMessage
  }
}
